import React, { useEffect } from "react";
import {
  RotateCcw,
  Bold,
  Italic,
  List,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Heading3,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Heading from "@tiptap/extension-heading";
import { Box, Typography, TextField, IconButton, Paper } from "@mui/material";

const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        pb: 1,
        mb: 1,
        display: "flex",
        gap: 1,
      }}
    >
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        color={editor.isActive("heading", { level: 1 }) ? "primary" : "default"}
      >
        <Heading1 />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        color={editor.isActive("heading", { level: 2 }) ? "primary" : "default"}
      >
        <Heading2 />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        color={editor.isActive("heading", { level: 3 }) ? "primary" : "default"}
      >
        <Heading3 />
      </IconButton>
      <Box sx={{ width: 1, mx: 1, bgcolor: "divider" }} />
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleBold().run()}
        color={editor.isActive("bold") ? "primary" : "default"}
      >
        <Bold />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        color={editor.isActive("italic") ? "primary" : "default"}
      >
        <Italic />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        color={editor.isActive("bulletList") ? "primary" : "default"}
      >
        <List />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => {
          const url = window.prompt("Enter URL");
          if (url) {
            editor.chain().focus().setLink({ href: url }).run();
          }
        }}
        color={editor.isActive("link") ? "primary" : "default"}
      >
        <LinkIcon />
      </IconButton>
    </Box>
  );
};

export function ConfigSection({
  title,
  field,
  onChange,
  onUndo,
  isRichText = false,
  isMultiline = false,
}) {
  const hasChanges = field.edited !== null && field.edited !== field.original;
  const currentValue = field.edited ?? field.original;
  const isInvalid = field.max_length && currentValue.length > field.max_length;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      Heading.configure({
        levels: [1, 2, 3],
      }),
    ],
    content: currentValue,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        style:
          "outline: none; min-height: 200px; max-height: 400px; overflow-y: auto;",
      },
    },
  });

  useEffect(() => {
    if (editor && currentValue !== editor.getHTML()) {
      editor.commands.setContent(currentValue);
    }
  }, [currentValue, editor]);

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1" component="label">
          {title}
          {field.max_length && (
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              ({currentValue.length}/{field.max_length})
            </Typography>
          )}
        </Typography>
        {hasChanges && (
          <IconButton
            size="small"
            onClick={onUndo}
            color="primary"
            sx={{ p: 0.5 }}
          >
            <RotateCcw />
          </IconButton>
        )}
      </Box>

      {isRichText ? (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <MenuBar editor={editor} />
          <Box sx={{ p: 2 }}>
            <EditorContent editor={editor} />
          </Box>
        </Paper>
      ) : (
        <TextField
          fullWidth
          multiline={isMultiline}
          rows={isMultiline ? 4 : 1}
          value={currentValue}
          onChange={(e) => onChange(e.target.value)}
          error={isInvalid}
          helperText={
            isInvalid
              ? `Text exceeds maximum length of ${field.max_length} characters`
              : ""
          }
          variant="outlined"
          size="small"
        />
      )}
    </Box>
  );
}
